import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based。
  // PDF 題號 ↔ JSON 題號：9a-9d=q23-q26、12=q30、13=q33、14-20=q34-q43、21-27=q44-q52、
  //   30=q56、31=q57、33-1~3=q59-q61、34=q62、35A-E=q63-q72（每組「有沒有搭乘」+「困難」）、
  //   36a=q74、37a=q76、38/39=q78/q79、40=q80、41=q82、41O=q83、41a=q84、42=q85-q92、
  //   43-51=q93-q108、60=q125、63=q133、64=q136、68=q146、70=q163、72=q168、
  //   73=q171-q176、75=q179-q184、76=q185、78=q188、82=q192、F=q196、F1-F3=q197-q199。

  // E1 舊制障礙類別(q7) 第9項＝植物人：第12題、陸章工作現況免填答，且第72題後結束訪查
  const notPlantPerson = '{q7} <> 9'
  // 2題 出生年(q11)：第1項是民國0年(115歲)、第116項是民國115年(0歲)，
  // 故「足歲年齡 = 116 - 選項序號」，年齡門檻一律換算成序號區間。
  const isAge3AndUp = '{q11} <= 113' // 3歲以上（未滿3歲＝114-116項）
  const isAge3To5 = '({q11} >= 111 and {q11} <= 113)' // 3歲至未滿6歲
  const isAge6AndUp = '{q11} <= 110' // 6歲以上
  const isAge12AndUp = '{q11} <= 104' // 12歲及以上
  const isAge15AndUp = '{q11} <= 101' // 15歲以上（工作現況）
  const isAge25To74 = '({q11} >= 42 and {q11} <= 91)' // 25歲至未滿75歲（癌症篩檢）
  // 76題 限民國93年1月13日前(含當日)出生；JSON 只到「月」，93年1月整月視為符合
  const bornBeforeElectionDay = '(({q11} <= 93) or ({q11} = 94 and {q12} = 1))'
  // 13題 居住地點：(1)一般住宅續問 14-20 題；(2)住宿式機構續問 21-27 題
  const livesAtHome = '{q33} = 1'
  const livesInFacility = '{q33} = 2'
  // 30題 (5)都沒有外出者，31-33 題免答
  const goesOut = '({q56} = 1 or {q56} = 2 or {q56} = 3 or {q56} = 4)'
  // 40題 (1)可以獨立自我照顧 → 跳問 52 題，故 40a-51 題都要「非(1)」
  const needsCare = '({q80} = 2 or {q80} = 3)'
  // 43-51 題另限「住一般住宅者填答」
  const homeServiceSection = '(' + livesAtHome + ') and ' + needsCare
  // 陸章 60-62 題：15歲以下、障礙類別為植物人者免填答
  const workSection = '(' + notPlantPerson + ') and (' + isAge15AndUp + ')'
  // 19題 12 項設施任一選「(2)需要」→ 續問 19a
  const needsAnyFacility =
    '({q40.1} = 2) or ({q40.2} = 2) or ({q40.3} = 2) or ({q40.4} = 2) or ' +
    '({q40.5} = 2) or ({q40.6} = 2) or ({q40.7} = 2) or ({q40.8} = 2) or ' +
    '({q40.9} = 2) or ({q40.10} = 2) or ({q40.11} = 2) or ({q40.12} = 2)'
  // 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
  const mainCarerIsFamily = '({q84} >= 1 and {q84} <= 12)'

  const visibleIfConditionsInit = convertVisibleIf({
    // 7.1 未滿3歲不用填答；3歲至未滿6歲填學齡前兩項(q17)，6歲以上填(3)~(10)(q18)
    q17: isAge3To5,
    q18: isAge6AndUp,
    // 8. (2)有配偶/(3)與人同居 → 續問 8a、8b；(1)未婚等其餘選項跳問第 9 題
    q20: '{q19} = 2 or {q19} = 3',
    q21: '{q19} = 2 or {q19} = 3',
    // 9. (2)有子女 → 續問 9a-9d 四個人數欄
    q23: '{q22} = 2',
    q24: '{q22} = 2',
    q25: '{q22} = 2',
    q26: '{q22} = 2',
    // 12. 3歲以下、障礙類別為植物人者免填答，跳問第 13 題
    q30: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    q31: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    // 14-20. 限 13 題選(1)一般住宅
    q34: livesAtHome,
    q35: livesAtHome,
    q36: livesAtHome,
    q37: livesAtHome,
    q38: livesAtHome,
    // 18. (2)與他人同住 是 o_type 6，值為 "other"，不可寫成 2
    q39: '(' + livesAtHome + ') and ({q38} = "other")',
    q40: livesAtHome,
    // 19. 第 12 列「其他」需要時，才問是哪種設施
    q41: '(' + livesAtHome + ') and ({q40.12} = 2)',
    // 19a. 逐列對應 19 題：該列選「(2)需要」，19a 才出現同一列
    q42: '(' + livesAtHome + ') and (' + needsAnyFacility + ')',
    'q42.row1': '{q40.1} = 2',
    'q42.row2': '{q40.2} = 2',
    'q42.row3': '{q40.3} = 2',
    'q42.row4': '{q40.4} = 2',
    'q42.row5': '{q40.5} = 2',
    'q42.row6': '{q40.6} = 2',
    'q42.row7': '{q40.7} = 2',
    'q42.row8': '{q40.8} = 2',
    'q42.row9': '{q40.9} = 2',
    'q42.row10': '{q40.10} = 2',
    'q42.row11': '{q40.11} = 2',
    'q42.row12': '{q40.12} = 2',
    q43: livesAtHome,
    // 21-27. 限 13 題選(2)住宿式機構
    q44: livesInFacility,
    q45: livesInFacility,
    q46: livesInFacility,
    // 23. (1)沒有家人或親屬 → 跳問第 25 題；(2)有 → 續問 23a、24
    q47: '(' + livesInFacility + ') and ({q46} = 2)',
    q48: '(' + livesInFacility + ') and ({q46} = 2)',
    q49: livesInFacility,
    q50: livesInFacility,
    q51: livesInFacility,
    q52: livesInFacility,
    // 31-33. 30 題選(5)都沒有外出者免答
    q57: goesOut,
    q58: goesOut,
    q59: goesOut,
    q60: goesOut,
    q61: goesOut,
    // 34. 限 30 題選(4)很少外出或(5)都沒有外出
    q62: '{q56} = 4 or {q56} = 5',
    // 35A-E. 各組先問「(01)有搭乘」，有搭乘才問困難題
    q64: '{q63} = 1',
    q66: '{q65} = 1',
    q68: '{q67} = 1',
    q70: '{q69} = 1',
    q72: '{q71} = 1',
    // 36. (1)有使用通用計程車 → 續問 36a
    q74: '{q73} = 1',
    // 37. (1)有申請復康巴士 → 續問 37a
    q76: '{q75} = 1',
    // 38、39. 未滿6歲者免答，跳問第 40 題
    q78: isAge6AndUp,
    q79: isAge6AndUp,
    // 40. (1)可以獨立自我照顧 → 跳問第 52 題
    q81: needsCare,
    q82: needsCare,
    // 41O. 41 題勾選(12)其他親戚 → 續問說明
    q83: needsCare + ' and ({q82} anyof [12])',
    q84: needsCare,
    // 42. 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
    q85: needsCare + ' and ' + mainCarerIsFamily,
    q86: needsCare + ' and ' + mainCarerIsFamily,
    q87: needsCare + ' and ' + mainCarerIsFamily,
    q88: needsCare + ' and ' + mainCarerIsFamily,
    q89: needsCare + ' and ' + mainCarerIsFamily,
    q90: needsCare + ' and ' + mainCarerIsFamily,
    q91: needsCare + ' and ' + mainCarerIsFamily,
    q92: needsCare + ' and ' + mainCarerIsFamily,
    // 43-51. 住一般住宅者填答，未住一般住宅者跳問第 52 題
    q93: homeServiceSection,
    // 43. (1)不需要居家服務 → 跳問第 46 題（43a-43d、44、45 皆不問）
    q94: homeServiceSection + ' and ({q93} = 2)',
    q95: homeServiceSection + ' and ({q93} = 2)',
    q96: homeServiceSection + ' and ({q93} = 2)',
    q97: homeServiceSection + ' and ({q93} = 2)',
    q98: homeServiceSection + ' and ({q93} = 2)',
    // 44. (1)有申請且符合需要 → 跳問第 46 題，故第 45 題限(2)(3)
    q99: homeServiceSection + ' and ({q93} = 2) and ({q98} = 2 or {q98} = 3)',
    q100: homeServiceSection,
    // 46. (1)不需要日間服務 → 跳問第 49 題（46a-46c、47、48 皆不問）
    q101: homeServiceSection + ' and ({q100} = 2)',
    q102: homeServiceSection + ' and ({q100} = 2)',
    q103: homeServiceSection + ' and ({q100} = 2)',
    q104: homeServiceSection + ' and ({q100} = 2)',
    // 47. (1)有申請且符合需要 → 跳問第 49 題，故第 48 題限(2)(3)
    q105:
      homeServiceSection + ' and ({q100} = 2) and ({q104} = 2 or {q104} = 3)',
    q106: homeServiceSection,
    // 49. (1)有申請且符合需要 → 跳問第 51 題，故第 50 題限(2)(3)
    q107: homeServiceSection + ' and ({q106} = 2 or {q106} = 3)',
    q108: homeServiceSection,
    // 陸、工作狀況 章節標題：整章(60-62 題)都被隱藏時，標題也不顯示
    q124: workSection,
    // 60-62. 15歲以下、障礙類別為植物人者免填答，跳問第 63 題
    q125: workSection,
    // 60. (1)有在做工作 → 續問 60a，答完跳問第 62 題
    q126: workSection + ' and ({q125} = 1)',
    // 60. (2)無工作但想工作且在找工作 → 續問 60b
    q127: workSection + ' and ({q125} = 2)',
    // 60b. (2)不能立刻開始工作 → 續問原因
    q128: workSection + ' and ({q125} = 2) and ({q127} = 2)',
    // 60. (5)不想工作 → 續問 60c，答完跳問第 63 題
    q129: workSection + ' and ({q125} = 5)',
    // 61. 只有 60 題選(2)或(3)者會走到；(1)跳 62、(4)跳 63、(5)走 60c 後跳 63
    q130: workSection + ' and ({q125} = 2 or {q125} = 3)',
    // 62. 只有 60 題選(1)有在做工作者作答
    q131: workSection + ' and ({q125} = 1)',
    // 63. (1)有做 → 63a；(2)沒有做過 → 63b；(98)不知道 → 跳問第 64 題
    q134: '{q133} = 1',
    q135: '{q133} = 2',
    // 64. 未滿25歲或75歲以上者免答，跳問第 65 題
    q136: isAge25To74,
    // 64. (1)有做 → 64a；(2)沒有做過 → 64b；(98)不知道 → 跳問第 65 題
    q137: isAge25To74 + ' and ({q136} = 1)',
    q138: isAge25To74 + ' and ({q136} = 2)',
    // 65. (1)需要定期就醫 → 續問 65a；(2)不需要 → 跳問第 66 題
    q140: '{q139} = 1',
    // 66. (2)需要復健治療 → 續問 66a、66b；(1)不需要 → 跳問第 67 題
    q142: '{q141} = 2',
    q143: '{q141} = 2',
    // 67. (2)診所 → 續問 67a
    q145: '{q144} = 2',
    // 68. (1)完全沒有困難 → 跳問第 69 題；68a1-3、68b1-3 醫院/診所/衛生所三組都問
    q147: '{q146} = 2',
    q148: '{q146} = 2',
    q149: '{q146} = 2',
    q150: '{q146} = 2',
    // 68b 勾(3)缺乏無障礙環境 → 續問項目；勾(6)缺乏溝通支持服務 → 續問說明與建議
    q151: '({q146} = 2) and ({q150} anyof [3])',
    q152: '({q146} = 2) and ({q150} anyof [6])',
    q153: '({q146} = 2) and ({q150} anyof [6])',
    q154: '{q146} = 2',
    q155: '({q146} = 2) and ({q154} anyof [3])',
    q156: '({q146} = 2) and ({q154} anyof [6])',
    q157: '({q146} = 2) and ({q154} anyof [6])',
    q158: '{q146} = 2',
    q159: '({q146} = 2) and ({q158} anyof [3])',
    q160: '({q146} = 2) and ({q158} anyof [6])',
    q161: '({q146} = 2) and ({q158} anyof [6])',
    // 70. (1)需要且有使用 → 70a；(2)需要但未使用 → 70b、70c
    q164: '{q163} = 1',
    q165: '{q163} = 2',
    q166: '{q163} = 2',
    // 72. (4)不好或(5)很不好 → 續問 72a
    q169: '{q168} = 4 or {q168} = 5',
    // 捌、休閒、社會參與及自我決定 章節標題：整章(73-82 題)都被隱藏時，標題也不顯示
    q170: notPlantPerson,
    // 73 題以後：障礙類別為植物人者於第 72 題後結束訪查
    q171: notPlantPerson,
    q172: notPlantPerson,
    // 73-2a~73-2d. 73-2 該列選「(4)無」→ 續問該活動不想參與的主要原因
    q173: '(' + notPlantPerson + ') and ({q172.1} = 2)',
    q174: '(' + notPlantPerson + ') and ({q172.2} = 2)',
    q175: '(' + notPlantPerson + ') and ({q172.3} = 2)',
    q176: '(' + notPlantPerson + ') and ({q172.4} = 2)',
    q177: notPlantPerson,
    // 74. (2)有遭遇困難 → 續問 74a
    q178: '(' + notPlantPerson + ') and ({q177} = 2)',
    q179: notPlantPerson,
    q180: notPlantPerson,
    // 75-2a~75-2d. 75-2 該列選「(2)沒有」→ 續問該社團不想參與的主要原因
    q181: '(' + notPlantPerson + ') and ({q180.1} = 2)',
    q182: '(' + notPlantPerson + ') and ({q180.2} = 2)',
    q183: '(' + notPlantPerson + ') and ({q180.3} = 2)',
    q184: '(' + notPlantPerson + ') and ({q180.4} = 2)',
    // 76. 限民國93年1月13日前(含當日)出生者作答
    q185: '(' + notPlantPerson + ') and ' + bornBeforeElectionDay,
    // 76. (2)沒有去投票 → 續問 76a
    q186: '(' + notPlantPerson + ') and ({q185} = 2)',
    // 77. (3)沒有投票權者跳問第 78 題
    q187: '(' + notPlantPerson + ') and ({q185} = 1 or {q185} = 2)',
    // 78. 限12歲及以上作答
    q188: '(' + notPlantPerson + ') and (' + isAge12AndUp + ')',
    q189: notPlantPerson,
    q190: notPlantPerson,
    q191: notPlantPerson,
    // 82. (2)不願意回答 → 82a-82c 皆不顯示，問卷自然結束
    q192: notPlantPerson,
    // 82a. 僅調查女性(第 1 題選(2)女)，其餘直接跳答 82b
    q193: '(' + notPlantPerson + ') and ({q192} = 1) and ({q10} = 2)',
    // 82b. 男性直接作答；女性限 82a 選(2)沒有（選(1)有則跳 82c）
    q194:
      '(' +
      notPlantPerson +
      ') and ({q192} = 1) and (({q10} <> 2) or ({q193} = 2))',
    // 82c. 82a 或 82b 任一選(1)有
    q195:
      '(' +
      notPlantPerson +
      ') and ({q192} = 1) and (({q193} = 1) or ({q194} = 1))',
    // F. (3)大部分非身心障礙者本人回答 → 續問 F1~F3
    // 訪員填寫欄位不受「植物人結束訪查」影響，一律作答
    q197: '{q196} = 3',
    q198: '{q196} = 3',
    q199: '{q196} = 3',
  })
  // 章節標題頁（壹~捌）無選項，以及意見欄、可接受複查時段，一律非必答
  const notRequired = convertNotRequired([
    9, // 壹、基本資料
    32, // 貳、居住狀況
    55, // 參、外出及交通狀況
    77, // 肆、起居生活狀況
    109, // 伍、經濟狀況
    124, // 陸、工作狀況
    132, // 柒、健康及醫療照顧
    170, // 捌、休閒、社會參與及自我決定
    200, // 意見欄
    201, // 可接受複查時段
  ])
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  // 帶出來源(前題) → 帶入目標(後題)：後題選項＝前題已勾選的選項
  const questionMappings = convertQuestionMappings({
    q30: 'q31', // 12.溝通方式(複選) → 12a.以上最主要溝通方式
    q82: 'q84', // 41.提供協助或照顧的人(複選) → 41a.其中最主要的照顧者
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 本案為政府委託訪問調查，結語採客戶指定文案，不使用 EZChoice 預設結語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(`<div>問卷已成功送出，請致贈受訪者禮券，謝謝。</div>`)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
